import React, { useEffect, useState } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps, createStatusChip, statusConfigs } from '../../utils/tableUtils';

export default function UserSubscriptionManagementPage() {
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ user_email: '', plan_label: '', payment_status: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [addOns, setAddOns] = useState({}); // {addonId: qty}
  const [form, setForm] = useState({
    user: '',
    pricing_plan: '',
    is_active: true,
    auto_renew: true,
    start_date: '',
    end_date: '',
    payment_status: 'active',
    change_reason: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptionTab, setSubscriptionTab] = useState('active'); // 'active' | 'history'
  const navigate = useNavigate();

  // Users with at least one subscription (for filter autocomplete)
  const userEmailsWithHistory = React.useMemo(() => {
    const emails = [...new Set((subs || []).map(s => s.user_email).filter(Boolean))];
    return emails.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [subs]);

  // Active tab: only is_active subscriptions; History tab: all
  const subsForTab = React.useMemo(() => {
    if (subscriptionTab === 'active') return (subs || []).filter(s => s.is_active === true);
    return subs || [];
  }, [subs, subscriptionTab]);

  const filteredSubs = filterData(subsForTab, filters);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPaginated = async (endpoint) => {
    let url = endpoint;
    let all = [];
    while (url) {
      const res = await api.get(url);
      const data = res.data;
      if (Array.isArray(data)) {
        all = data;
        break;
      }
      all = all.concat(data.results || []);
      url = data.next || null;
    }
    return all;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [allSubs, allPlans, allUsers, cat] = await Promise.all([
        fetchPaginated('/accounts/user-subscriptions/'),
        fetchPaginated('/accounts/pricing-plans/'),
        fetchPaginated('/accounts/admin/users/?is_active=true'),
        fetchPaginated('/accounts/addon-catalog/')
      ]);
      setSubs(allSubs);
      setPlans(allPlans);
      setUsers(allUsers);
      setCatalog(cat.filter(a => a.is_active));
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { user_email: '', plan_label: '', payment_status: '' });

  const filterConfig = [
    {
      field: 'user_email',
      label: 'User Email',
      type: 'autocomplete',
      placeholder: 'Select or type user email...',
      options: userEmailsWithHistory,
      getOptionLabel: (option) => option || ''
    },
    {
      field: 'plan_label',
      label: 'Pricing Plan',
      type: 'select',
      options: plans.map(plan => ({ value: plan.name, label: plan.name }))
    },
    {
      field: 'payment_status',
      label: 'Payment Status',
      type: 'select',
      options: [
        { value: '', label: 'All statuses' },
        { value: 'active', label: 'Completed' },
        { value: 'unpaid', label: 'Declined' },
        { value: 'past_due', label: 'Past due' },
        { value: 'canceled', label: 'Canceled' },
        { value: 'trialing', label: 'Trialing' }
      ]
    }
  ];

  const handleOpen = async (item = null) => {
    if (item) {
      setForm({
        user: item.user,
        pricing_plan: item.pricing_plan,
        is_active: item.is_active,
        auto_renew: item.auto_renew,
        start_date: item.start_date ? item.start_date.slice(0, 10) : '',
        end_date: item.end_date ? item.end_date.slice(0, 10) : '',
        payment_status: item.payment_status,
        change_reason: ''
      });
      setEditMode(true);
      setCurrentId(item.id);
      setSelectedUser(users.find(u => u.id === item.user));
    } else {
      setForm({
        user: '',
        pricing_plan: '',
        is_active: true,
        auto_renew: true,
        start_date: '',
        end_date: '',
        payment_status: 'active',
        change_reason: ''
      });
      setEditMode(false);
      setCurrentId(null);
      setSelectedUser(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({
      user: '',
      pricing_plan: '',
      is_active: true,
      auto_renew: true,
      start_date: '',
      end_date: '',
      payment_status: 'active',
      change_reason: ''
    });
    setEditMode(false);
    setCurrentId(null);
    setSelectedUser(null);
  };

  const handleSubmit = async () => {
    if (!form.user || !form.pricing_plan) {
      showSnackbar('Please select both user and pricing plan', 'error');
      return;
    }

    try {
      const payload = {
        ...form,
        addons: Object.entries(addOns).map(([id, qty]) => ({ addon: id, quantity: qty }))
      };

      if (editMode) {
        await api.patch(`/accounts/user-subscriptions/${currentId}/`, payload);
        showSnackbar('Subscription updated successfully');
      } else {
        await api.post('/accounts/user-subscriptions/', payload);
        showSnackbar('Subscription created successfully');
      }
      handleClose();
      fetchAll();
    } catch (err) {
      console.error(err);
      showSnackbar('Operation failed', 'error');
    }
  };

  const columns = [
    {
      field: 'user_email',
      headerName: 'User',
      flex: 2,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.user_name || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'plan_label',
      headerName: 'Plan',
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value || 'No Plan'} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'payment_status',
      headerName: 'Payment Status',
      flex: 1,
      renderCell: (params) => {
        const status = (params.value || '').toLowerCase();
        const paymentStatusConfig = {
          active: { label: 'Completed', color: 'success' },
          unpaid: { label: 'Declined', color: 'error' },
          past_due: { label: 'Past due', color: 'warning' },
          canceled: { label: 'Canceled', color: 'default' },
          trialing: { label: 'Trialing', color: 'info' },
          incomplete: { label: 'Incomplete', color: 'warning' },
          incomplete_expired: { label: 'Expired', color: 'default' }
        };
        const config = paymentStatusConfig[status] || { label: (params.value || '—'), color: 'default' };
        return <Chip label={config.label} size="small" color={config.color} variant="outlined" />;
      }
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      flex: 1,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    ...(subscriptionTab === 'active'
      ? [
          {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => handleOpen(params.row)}
                    sx={{ p: 0.5 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          }
        ]
      : [])
  ];

  const actionButtons = [
    {
      label: 'Add Subscription',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: subscriptionTab === 'active' ? 'No active subscriptions' : 'No subscription history',
    description: subscriptionTab === 'active'
      ? 'Only subscriptions marked active are shown. Try the History tab or adjust filters.'
      : 'Try adjusting your filters or add your first subscription.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="User Subscriptions"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredSubs.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={subscriptionTab} onChange={(_, v) => setSubscriptionTab(v)}>
            <Tab label="Active" value="active" />
            <Tab label="History" value="history" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>
        <DataGrid
          rows={filteredSubs}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Subscription' : 'Add Subscription'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>User</InputLabel>
                <Select
                  value={form.user}
                  label="User"
                  onChange={(e) => {
                    setForm({ ...form, user: e.target.value });
                    setSelectedUser(users.find(u => u.id === e.target.value));
                  }}
                >
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.email} ({user.first_name} {user.last_name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Pricing Plan</InputLabel>
                <Select
                  value={form.pricing_plan}
                  label="Pricing Plan"
                  onChange={(e) => setForm({ ...form, pricing_plan: e.target.value })}
                >
                  {plans.map(plan => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={form.payment_status}
                  label="Payment Status"
                  onChange={(e) => setForm({ ...form, payment_status: e.target.value })}
                >
                  <MenuItem value="active">Completed</MenuItem>
                  <MenuItem value="unpaid">Declined</MenuItem>
                  <MenuItem value="past_due">Past due</MenuItem>
                  <MenuItem value="canceled">Canceled</MenuItem>
                  <MenuItem value="trialing">Trialing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  Active
                </Typography>
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  Auto Renew
                </Typography>
                <Switch
                  checked={form.auto_renew}
                  onChange={(e) => setForm({ ...form, auto_renew: e.target.checked })}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Change Reason"
                value={form.change_reason}
                onChange={(e) => setForm({ ...form, change_reason: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
